import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateMapNoteEntryInput,
  MapNote,
  MapNoteEntry,
  NoteAudience,
  UpdateMapNoteEntryInput,
} from "@west-marches/shared";
import {
  createChildEntry,
  deleteEntry,
  getEntry,
  getRootEntry,
  listChildEntries,
  updateEntry,
} from "@/lib/mapNoteEntryClient";
import { queryKeys } from "@/lib/queryKeys";

export function useRootEntryQuery(
  campaignId: string | null,
  mapNoteId: string | null,
  audience: NoteAudience = "gm",
  options: { asPlayer?: boolean } = {},
) {
  const asPlayer = Boolean(options.asPlayer);
  return useQuery({
    queryKey: queryKeys.mapNoteEntries.root(mapNoteId ?? "", audience, asPlayer),
    queryFn: () => getRootEntry(campaignId!, mapNoteId!, audience, { asPlayer }),
    enabled: Boolean(campaignId) && Boolean(mapNoteId),
  });
}

/**
 * Prefetch root entries for all provided map notes so the popover never shows
 * "Loading…". Uses `prefetchQuery` which is a no-op when data is already
 * fresh, avoiding redundant network requests.
 */
export function usePrefetchMapNoteEntries(
  campaignId: string | null,
  mapNotes: MapNote[] | undefined,
  audience: NoteAudience = "player",
  options: { asPlayer?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const asPlayer = Boolean(options.asPlayer);

  useEffect(() => {
    if (!campaignId || !mapNotes?.length) return;

    for (const mapNote of mapNotes) {
      queryClient.prefetchQuery({
        queryKey: queryKeys.mapNoteEntries.root(mapNote.id, audience, asPlayer),
        queryFn: () => getRootEntry(campaignId, mapNote.id, audience, { asPlayer }),
      });
    }
  }, [campaignId, mapNotes, audience, asPlayer, queryClient]);
}

export function useEntryQuery(
  campaignId: string | null,
  mapNoteId: string | null,
  entryId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.mapNoteEntries.detail(entryId ?? ""),
    queryFn: () => getEntry(campaignId!, mapNoteId!, entryId!),
    enabled: Boolean(campaignId) && Boolean(mapNoteId) && Boolean(entryId),
  });
}

export function useChildEntriesQuery(
  campaignId: string | null,
  mapNoteId: string | null,
  parentEntryId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.mapNoteEntries.children(parentEntryId ?? ""),
    queryFn: () => listChildEntries(campaignId!, mapNoteId!, parentEntryId!),
    enabled: Boolean(campaignId) && Boolean(mapNoteId) && Boolean(parentEntryId),
  });
}

export function useUpdateEntryMutation(
  campaignId: string | null,
  mapNoteId: string | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: UpdateMapNoteEntryInput }) =>
      updateEntry(campaignId!, mapNoteId!, entryId, input),
    onSuccess(updatedEntry) {
      queryClient.setQueryData<MapNoteEntry>(
        queryKeys.mapNoteEntries.detail(updatedEntry.id),
        updatedEntry,
      );
      if (!updatedEntry.parentEntryId) {
        queryClient.setQueryData<MapNoteEntry>(
          queryKeys.mapNoteEntries.root(updatedEntry.mapNoteId, updatedEntry.audience),
          updatedEntry,
        );
      }
      if (updatedEntry.parentEntryId) {
        queryClient.setQueryData<MapNoteEntry[] | undefined>(
          queryKeys.mapNoteEntries.children(updatedEntry.parentEntryId),
          (prev) =>
            prev
              ? prev.map((entry) => (entry.id === updatedEntry.id ? updatedEntry : entry))
              : prev,
        );
      }
      window.dispatchEvent(
        new CustomEvent("note-title-changed", {
          detail: { noteId: updatedEntry.id, title: updatedEntry.title },
        }),
      );
    },
  });
}

export function useCreateChildEntryMutation(
  campaignId: string | null,
  mapNoteId: string | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentEntryId,
      input,
    }: {
      parentEntryId: string;
      input: CreateMapNoteEntryInput;
    }) => createChildEntry(campaignId!, mapNoteId!, parentEntryId, input),
    onSuccess(createdEntry) {
      if (createdEntry.parentEntryId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.mapNoteEntries.children(createdEntry.parentEntryId),
        });
      }
    },
  });
}

export function useDeleteEntryMutation(
  campaignId: string | null,
  mapNoteId: string | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => deleteEntry(campaignId!, mapNoteId!, entryId),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["map-note-entries"] });
    },
  });
}
